interface AlignableElement {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PositionUpdate {
  id: string;
  x?: number;
  y?: number;
}

const getBoundingBox = (elements: AlignableElement[]) => {
  if (elements.length === 0) return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };

  let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;
  for (const el of elements) {
    left = Math.min(left, el.x);
    top = Math.min(top, el.y);
    right = Math.max(right, el.x + el.width);
    bottom = Math.max(bottom, el.y + el.height);
  }
  return { left, top, right, bottom, width: right - left, height: bottom - top };
};

export const alignLeft = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  return elements.map((el) => ({ id: el.id, x: bbox.left }));
};

export const alignCenterH = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  const centerX = bbox.left + bbox.width / 2;
  return elements.map((el) => ({ id: el.id, x: centerX - el.width / 2 }));
};

export const alignRight = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  return elements.map((el) => ({ id: el.id, x: bbox.right - el.width }));
};

export const alignTop = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  return elements.map((el) => ({ id: el.id, y: bbox.top }));
};

export const alignMiddleV = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  const centerY = bbox.top + bbox.height / 2;
  return elements.map((el) => ({ id: el.id, y: centerY - el.height / 2 }));
};

export const alignBottom = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 2) return [];
  const bbox = getBoundingBox(elements);
  return elements.map((el) => ({ id: el.id, y: bbox.bottom - el.height }));
};

export const distributeHorizontally = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 3) return [];

  const sorted = [...elements].sort((a, b) => a.x - b.x);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const totalWidth = (last.x + last.width) - first.x;
  const totalElementWidth = sorted.reduce((sum, el) => sum + el.width, 0);
  const gap = (totalWidth - totalElementWidth) / (sorted.length - 1);

  let currentX = first.x;
  return sorted.map((el, i) => {
    const update: PositionUpdate = { id: el.id };
    if (i === 0) {
      currentX = el.x + el.width + gap;
      return update;
    }
    if (i === sorted.length - 1) {
      return update;
    }
    update.x = currentX;
    currentX = currentX + el.width + gap;
    return update;
  });
};

export const alignToArtboard = (
  element: AlignableElement,
  direction: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom',
  artboardWidth: number,
  artboardHeight: number
): PositionUpdate => {
  switch (direction) {
    case 'left':
      return { id: element.id, x: 0 };
    case 'centerH':
      return { id: element.id, x: (artboardWidth - element.width) / 2 };
    case 'right':
      return { id: element.id, x: artboardWidth - element.width };
    case 'top':
      return { id: element.id, y: 0 };
    case 'centerV':
      return { id: element.id, y: (artboardHeight - element.height) / 2 };
    case 'bottom':
      return { id: element.id, y: artboardHeight - element.height };
  }
};

export const distributeVertically = (elements: AlignableElement[]): PositionUpdate[] => {
  if (elements.length < 3) return [];

  const sorted = [...elements].sort((a, b) => a.y - b.y);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const totalHeight = (last.y + last.height) - first.y;
  const totalElementHeight = sorted.reduce((sum, el) => sum + el.height, 0);
  const gap = (totalHeight - totalElementHeight) / (sorted.length - 1);

  let currentY = first.y;
  return sorted.map((el, i) => {
    const update: PositionUpdate = { id: el.id };
    if (i === 0) {
      currentY = el.y + el.height + gap;
      return update;
    }
    if (i === sorted.length - 1) {
      return update;
    }
    update.y = currentY;
    currentY = currentY + el.height + gap;
    return update;
  });
};

type EdgeAxis = 'x' | 'y';
type EdgeAnchor = 'start' | 'center' | 'end';

const distributeEdges = (
  elements: AlignableElement[],
  axis: EdgeAxis,
  anchor: EdgeAnchor
): PositionUpdate[] => {
  if (elements.length < 3) return [];
  const pos = axis === 'x' ? 'x' : 'y';
  const dim = axis === 'x' ? 'width' : 'height';

  const anchorOf = (el: AlignableElement): number => {
    const p = el[pos];
    const s = el[dim];
    if (anchor === 'start') return p;
    if (anchor === 'end') return p + s;
    return p + s / 2;
  };

  const sorted = [...elements].sort((a, b) => anchorOf(a) - anchorOf(b));
  const firstAnchor = anchorOf(sorted[0]);
  const lastAnchor = anchorOf(sorted[sorted.length - 1]);
  const span = lastAnchor - firstAnchor;
  const step = span / (sorted.length - 1);

  return sorted.map((el, i) => {
    if (i === 0 || i === sorted.length - 1) return { id: el.id };
    const targetAnchor = firstAnchor + step * i;
    let newPos = targetAnchor;
    if (anchor === 'center') newPos = targetAnchor - el[dim] / 2;
    else if (anchor === 'end') newPos = targetAnchor - el[dim];
    return { id: el.id, [pos]: newPos } as PositionUpdate;
  });
};

export const distributeEdgesLeft = (els: AlignableElement[]) => distributeEdges(els, 'x', 'start');
export const distributeEdgesCenterH = (els: AlignableElement[]) => distributeEdges(els, 'x', 'center');
export const distributeEdgesRight = (els: AlignableElement[]) => distributeEdges(els, 'x', 'end');
export const distributeEdgesTop = (els: AlignableElement[]) => distributeEdges(els, 'y', 'start');
export const distributeEdgesMiddleV = (els: AlignableElement[]) => distributeEdges(els, 'y', 'center');
export const distributeEdgesBottom = (els: AlignableElement[]) => distributeEdges(els, 'y', 'end');

export const alignAllToArtboard = (
  elements: AlignableElement[],
  direction: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom',
  artboardWidth: number,
  artboardHeight: number
): PositionUpdate[] => {
  if (elements.length === 0) return [];
  return elements.map((el) =>
    alignToArtboard(el, direction, artboardWidth, artboardHeight)
  );
};
